
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/base/Button';

interface ContentItem {
  id: string;
  title: string;
  content: string;
  type: 'text' | 'image' | 'service';
}

interface Review {
  id: string;
  name: string;
  email: string;
  location: string;
  service: string;
  rating: number;
  review_text: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

interface ContactRequest {
  id: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  status: 'pending' | 'responded' | 'archived';
  created_at: string;
  updated_at: string;
}

export default function Admin() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('content');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Reviews management state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // Contact requests management state
  const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
  const [contactRequestsLoading, setContactRequestsLoading] = useState(false);
  const [contactRequestFilter, setContactRequestFilter] = useState<'all' | 'pending' | 'responded' | 'archived'>('all');

  // Mock content data - in a real app, this would come from a database
  const [contentItems, setContentItems] = useState<ContentItem[]>([
    {
      id: '1',
      title: 'Hero Title',
      content: 'Professional Painting Services in Atlanta',
      type: 'text'
    },
    {
      id: '2',
      title: 'Hero Description',
      content: 'Transform your property with our expert interior and exterior painting services.',
      type: 'text'
    },
    {
      id: '3',
      title: 'About Description',
      content: 'With years of experience serving the Atlanta area, we are committed to delivering exceptional painting services.',
      type: 'text'
    }
  ]);

  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [newContent, setNewContent] = useState('');

  useEffect(() => {
    if (isAuthenticated && activeTab === 'reviews') {
      fetchReviews();
    }
    if (isAuthenticated && activeTab === 'contact-requests') {
      fetchContactRequests();
    }
  }, [isAuthenticated, activeTab, reviewFilter, contactRequestFilter]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Simple password check - in production, use proper authentication
    if (password === 'WF2024@Y') {
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid password');
    }
  };

  const handleEditContent = (item: ContentItem) => {
    setEditingItem(item);
    setNewContent(item.content);
  };

  const handleSaveContent = () => {
    if (editingItem) {
      setContentItems(prev =>
        prev.map(item =>
          item.id === editingItem.id
            ? { ...item, content: newContent }
            : item
        )
      );
      setEditingItem(null);
      setNewContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setNewContent('');
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-reviews${reviewFilter !== 'all' ? `?status=${reviewFilter}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setReviews(result.reviews);
        } else {
          console.error('Failed to fetch reviews:', result.error);
        }
      } else {
        console.error('HTTP error:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchContactRequests = async () => {
    setContactRequestsLoading(true);
    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-contact-requests${contactRequestFilter !== 'all' ? `?status=${contactRequestFilter}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setContactRequests(result.requests);
        } else {
          console.error('Failed to fetch contact requests:', result.error);
        }
      } else {
        console.error('HTTP error:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching contact requests:', error);
    } finally {
      setContactRequestsLoading(false);
    }
  };

  const updateReviewStatus = async (reviewId: string, status: 'approved' | 'rejected') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-reviews`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'apikey': `${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ id: reviewId, status })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, response.statusText, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Update the review in the local state
        setReviews(prev =>
          prev.map(review =>
            review.id === reviewId
              ? { ...review, status, updated_at: new Date().toISOString() }
              : review
          )
        );
        console.log('Review status updated successfully');
      } else {
        console.error('Failed to update review status:', result.error || 'Unknown error');
        alert(`Failed to update review status: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating review status:', error instanceof Error ? error.message : String(error));
      alert('Network error occurred while updating review status. Please check your connection and try again.');
    }
  };

  const updateContactRequestStatus = async (requestId: string, status: 'responded' | 'archived') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-contact-requests`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'apikey': `${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ id: requestId, status })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, response.statusText, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Update the contact request in the local state
        setContactRequests(prev =>
          prev.map(request =>
            request.id === requestId
              ? { ...request, status, updated_at: new Date().toISOString() }
              : request
          )
        );
        console.log('Contact request status updated successfully');
      } else {
        console.error('Failed to update contact request status:', result.error || 'Unknown error');
        alert(`Failed to update contact request status: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating contact request status:', error instanceof Error ? error.message : String(error));
      alert('Network error occurred while updating contact request status. Please check your connection and try again.');
    }
  };

  const deleteReview = async (reviewId: string) => {
    if (!confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-reviews`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'apikey': `${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ id: reviewId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, response.statusText, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Remove the review from the local state
        setReviews(prev => prev.filter(review => review.id !== reviewId));
        console.log('Review deleted successfully');
      } else {
        console.error('Failed to delete review:', result.error || 'Unknown error');
        alert(`Failed to delete review: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting review:', error instanceof Error ? error.message : String(error));
      alert('Network error occurred while deleting review. Please check your connection and try again.');
    }
  };

  const deleteContactRequest = async (requestId: string) => {
    if (!confirm('Are you sure you want to delete this contact request?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-contact-requests`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'apikey': `${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ id: requestId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP error:', response.status, response.statusText, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        // Remove the contact request from the local state
        setContactRequests(prev => prev.filter(request => request.id !== requestId));
        console.log('Contact request deleted successfully');
      } else {
        console.error('Failed to delete contact request:', result.error || 'Unknown error');
        alert(`Failed to delete contact request: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting contact request:', error instanceof Error ? error.message : String(error));
      alert('Network error occurred while deleting contact request. Please check your connection and try again.');
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i
        key={index}
        className={`ri-star-${index < rating ? 'fill' : 'line'} text-yellow-400`}
      />
    ));
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      responded: 'bg-blue-100 text-blue-800 border-blue-200',
      archived: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusStyles[status as keyof typeof statusStyles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-fill text-white text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Admin Access</h1>
            <p className="text-gray-600 mt-2">Enter password to access admin panel</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter admin password"
                required
              />
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                {loginError}
              </div>
            )}

            <Button type="submit" className="w-full">
              <i className="ri-login-box-fill"></i>
              Login
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
              <p className="text-gray-600">Manage your website content and settings</p>
            </div>
            <Button
              onClick={() => setIsAuthenticated(false)}
              variant="outline"
              size="sm"
            >
              <i className="ri-logout-box-line"></i>
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <nav className="space-y-2">
                {[
                  { id: 'content', label: 'Content Management', icon: 'ri-file-text-fill' },
                  { id: 'reviews', label: 'Customer Reviews', icon: 'ri-star-fill' },
                  { id: 'contact-requests', label: 'Contact Requests', icon: 'ri-mail-fill' },
                  { id: 'images', label: 'Image Gallery', icon: 'ri-image-fill' },
                  { id: 'services', label: 'Services', icon: 'ri-service-fill' },
                  { id: 'settings', label: 'Settings', icon: 'ri-settings-3-fill' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors whitespace-nowrap cursor-pointer ${activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className={tab.icon}></i>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-8">
              {activeTab === 'content' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Content Management</h2>

                  <div className="space-y-6">
                    {contentItems.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                          <Button
                            onClick={() => handleEditContent(item)}
                            size="sm"
                            variant="outline"
                          >
                            <i className="ri-edit-line"></i>
                            Edit
                          </Button>
                        </div>

                        {editingItem?.id === item.id ? (
                          <div className="space-y-4">
                            <textarea
                              value={newContent}
                              onChange={(e) => setNewContent(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                              rows={4}
                            />
                            <div className="flex space-x-3">
                              <Button onClick={handleSaveContent} size="sm">
                                <i className="ri-save-line"></i>
                                Save
                              </Button>
                              <Button onClick={handleCancelEdit} size="sm" variant="outline">
                                <i className="ri-close-line"></i>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-600">{item.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-800">Customer Reviews Management</h2>
                    <div className="flex items-center space-x-4">
                      <select
                        value={reviewFilter}
                        onChange={(e) => setReviewFilter(e.target.value as any)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="all">All Reviews</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <Button onClick={fetchReviews} size="sm" variant="outline">
                        <i className="ri-refresh-line"></i>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {reviewsLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading reviews...</p>
                    </div>
                  ) : reviews.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <i className="ri-star-line text-4xl mb-4"></i>
                      <p>No reviews found for the selected filter.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {reviews.map((review) => (
                        <div key={review.id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center space-x-4 mb-2">
                                <h3 className="text-lg font-semibold text-gray-800">{review.name}</h3>
                                {getStatusBadge(review.status)}
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                                <span>{review.email}</span>
                                {review.location && <span>• {review.location}</span>}
                                {review.service && <span>• {review.service}</span>}
                              </div>
                              <div className="flex items-center mb-3">
                                {renderStars(review.rating)}
                                <span className="ml-2 text-sm text-gray-600">({review.rating}/5)</span>
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <div>Submitted: {new Date(review.created_at).toLocaleDateString()}</div>
                              {review.updated_at !== review.created_at && (
                                <div>Updated: {new Date(review.updated_at).toLocaleDateString()}</div>
                              )}
                            </div>
                          </div>

                          <blockquote className="text-gray-700 italic mb-4 p-4 bg-gray-50 rounded-lg">
                            "{review.review_text}"
                          </blockquote>

                          <div className="flex items-center space-x-3">
                            {review.status === 'pending' && (
                              <>
                                <Button
                                  onClick={() => updateReviewStatus(review.id, 'approved')}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <i className="ri-check-line"></i>
                                  Approve
                                </Button>
                                <Button
                                  onClick={() => updateReviewStatus(review.id, 'rejected')}
                                  size="sm"
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  <i className="ri-close-line"></i>
                                  Reject
                                </Button>
                              </>
                            )}
                            {review.status === 'approved' && (
                              <Button
                                onClick={() => updateReviewStatus(review.id, 'rejected')}
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <i className="ri-close-line"></i>
                                Reject
                              </Button>
                            )}
                            {review.status === 'rejected' && (
                              <Button
                                onClick={() => updateReviewStatus(review.id, 'approved')}
                                size="sm"
                                variant="outline"
                                className="border-green-300 text-green-600 hover:bg-green-50"
                              >
                                <i className="ri-check-line"></i>
                                Approve
                              </Button>
                            )}
                            <Button
                              onClick={() => deleteReview(review.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <i className="ri-delete-bin-line"></i>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'contact-requests' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-800">Contact Requests Management</h2>
                    <div className="flex items-center space-x-4">
                      <select
                        value={contactRequestFilter}
                        onChange={(e) => setContactRequestFilter(e.target.value as any)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="all">All Requests</option>
                        <option value="pending">Pending</option>
                        <option value="responded">Responded</option>
                        <option value="archived">Archived</option>
                      </select>
                      <Button onClick={fetchContactRequests} size="sm" variant="outline">
                        <i className="ri-refresh-line"></i>
                        Refresh
                      </Button>
                    </div>
                  </div>

                  {contactRequestsLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading contact requests...</p>
                    </div>
                  ) : contactRequests.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <i className="ri-mail-line text-4xl mb-4"></i>
                      <p>No contact requests found for the selected filter.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {contactRequests.map((request) => (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center space-x-4 mb-2">
                                <h3 className="text-lg font-semibold text-gray-800">{request.name}</h3>
                                {getStatusBadge(request.status)}
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                                <span>{request.email}</span>
                                {request.phone && <span>• {request.phone}</span>}
                                {request.service && <span>• {request.service}</span>}
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <div>Submitted: {new Date(request.created_at).toLocaleDateString()}</div>
                              {request.updated_at !== request.created_at && (
                                <div>Updated: {new Date(request.updated_at).toLocaleDateString()}</div>
                              )}
                            </div>
                          </div>

                          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Message:</h4>
                            <p className="text-gray-700">{request.message}</p>
                          </div>

                          <div className="flex items-center space-x-3">
                            {request.status === 'pending' && (
                              <>
                                <Button
                                  onClick={() => updateContactRequestStatus(request.id, 'responded')}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <i className="ri-check-line"></i>
                                  Mark as Responded
                                </Button>
                                <Button
                                  onClick={() => updateContactRequestStatus(request.id, 'archived')}
                                  size="sm"
                                  variant="outline"
                                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                >
                                  <i className="ri-archive-line"></i>
                                  Archive
                                </Button>
                              </>
                            )}
                            {request.status === 'responded' && (
                              <Button
                                onClick={() => updateContactRequestStatus(request.id, 'archived')}
                                size="sm"
                                variant="outline"
                                className="border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                <i className="ri-archive-line"></i>
                                Archive
                              </Button>
                            )}
                            {request.status === 'archived' && (
                              <Button
                                onClick={() => updateContactRequestStatus(request.id, 'responded')}
                                size="sm"
                                variant="outline"
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <i className="ri-reply-line"></i>
                                Mark as Responded
                              </Button>
                            )}
                            <a
                              href={`mailto:${request.email}?subject=Re: Your Contact Request&body=Hi ${request.name},%0D%0A%0D%0AThank you for contacting WF & Y Painting. We received your message regarding ${request.service || 'your inquiry'}.%0D%0A%0D%0A`}
                              className="inline-flex"
                            >
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-green-300 text-green-600 hover:bg-green-50"
                              >
                                <i className="ri-mail-line"></i>
                                Reply via Email
                              </Button>
                            </a>
                            <Button
                              onClick={() => deleteContactRequest(request.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <i className="ri-delete-bin-line"></i>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'images' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Image Gallery Management</h2>
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-image-line text-4xl mb-4"></i>
                    <p>Image management functionality would be implemented here.</p>
                    <p className="text-sm mt-2">Upload, organize, and manage website images.</p>
                  </div>
                </div>
              )}

              {activeTab === 'services' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Services Management</h2>
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-service-line text-4xl mb-4"></i>
                    <p>Services management functionality would be implemented here.</p>
                    <p className="text-sm mt-2">Add, edit, and organize service offerings.</p>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-6">Website Settings</h2>

                  <div className="space-y-8">
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                          <input
                            type="tel"
                            defaultValue="770-875-6990"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                          <input
                            type="email"
                            defaultValue="wr47090@gmail.com"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Hours</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Monday - Saturday</span>
                          <span className="text-gray-600">7:00 AM - 6:00 PM</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Sunday</span>
                          <span className="text-gray-600">Closed</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button>
                        <i className="ri-save-line"></i>
                        Save Settings
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
